import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { titleCase, fmtDate, can, MANAGE_TASKS } from "@/lib/format";
import { toast } from "sonner";
import { CheckSquare, Plus, Trash2 } from "lucide-react";

export default function AdminTasks() {
  const { user } = useAdminAuth();
  const editable = can(user?.role, MANAGE_TASKS);
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ title: "", description: "", priority: "medium", due_date: "" });

  const load = useCallback(async () => {
    const params = {}; if (filter !== "all") params.status = filter;
    const r = await api.get("/admin/tasks", { params });
    setTasks(r.data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.title.trim()) { toast.error("Title is required"); return; }
    try { await api.post("/admin/tasks", draft); toast.success("Task created"); setOpen(false); setDraft({ title: "", description: "", priority: "medium", due_date: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (t) => { try { await api.put(`/admin/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" }); load(); } catch { toast.error("Failed"); } };
  const remove = async (t) => { try { await api.delete(`/admin/tasks/${t.id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  const prColor = { high: "bg-red-100 text-red-800 border-red-200", medium: "bg-amber-100 text-amber-800 border-amber-200", low: "bg-[var(--panel-2)] text-[var(--text-2)] border-[var(--border-lch)]" };

  return (
    <div data-testid="admin-tasks">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><CheckSquare className="text-emerald-600" size={22} /><h1 className="text-2xl font-semibold">Follow-up Tasks</h1></div>
        {editable && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-task-btn" className="bg-[var(--panel-2)] text-[#c9a86a]"><Plus size={16} className="mr-2" />New Task</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Follow-up Task</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label className="text-xs">Title *</Label><Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="task-title" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Priority</Label>
                    <Select value={draft.priority} onValueChange={(v) => setDraft({ ...draft, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
                  <div className="space-y-1.5"><Label className="text-xs">Due Date</Label><Input type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="task-save" className="bg-[var(--panel-2)] text-[#c9a86a]">Create Task</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        {["open", "done", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-md text-sm ${filter === f ? "bg-[var(--panel-2)] text-[#c9a86a]" : "bg-[var(--panel)] border border-[var(--border-lch)] text-[var(--text-2)]"}`}>{titleCase(f)}</button>
        ))}
      </div>

      <div className="space-y-2">
        {tasks.map((t) => (
          <Card key={t.id} className="p-4 border-[var(--border-lch)] flex items-start gap-3" data-testid={`task-${t.id}`}>
            <Checkbox checked={t.status === "done"} onCheckedChange={() => editable && toggle(t)} className="mt-1" data-testid={`task-check-${t.id}`} />
            <div className="flex-1 min-w-0">
              <div className={`font-medium ${t.status === "done" ? "line-through text-[var(--muted)]" : ""}`}>{t.title}</div>
              {t.description && <div className="text-sm text-[var(--text-2)] mt-0.5">{t.description}</div>}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Badge variant="outline" className={prColor[t.priority]}>{titleCase(t.priority)}</Badge>
                {t.related_label && <Badge variant="outline" className="bg-[var(--panel-2)]">{titleCase(t.related_type)}: {t.related_label}</Badge>}
                {t.due_date && <span className="text-xs text-[var(--muted)]">Due {fmtDate(t.due_date)}</span>}
                <span className="text-xs text-[var(--muted)]">Created {fmtDate(t.created_at)}</span>
              </div>
            </div>
            {editable && <Button variant="ghost" size="sm" onClick={() => remove(t)} className="text-[var(--muted)] hover:text-red-600"><Trash2 size={16} /></Button>}
          </Card>
        ))}
        {!tasks.length && <Card className="p-8 text-center text-[var(--muted)] border-[var(--border-lch)]">No tasks here.</Card>}
      </div>
    </div>
  );
}
